import { useState, useRef, useContext, createContext, MutableRefObject } from 'react'
import { type EditorView } from 'prosemirror-view'

import { ourUuid } from 'utils'

interface Props {
  children: React.ReactNode
}

export type ProseViewType = MutableRefObject<EditorView | undefined>

export interface ProseContextProps {
  updateView: (view?: EditorView) => void
  currentView?: EditorView
  currentViewRef: ProseViewType
  clientUuid: string
  resetClientUuid: () => void
}

const defaultContext: ProseContextProps = {
  updateView: (view?: EditorView) => {},
  currentView: undefined,
  currentViewRef: { current: undefined },
  clientUuid: '',
  resetClientUuid: () => {},
}

export const ProseContext = createContext(defaultContext)

const ProseInstanceProvider = ({ children }: Props) => {
  const currentViewRef = useRef<EditorView>()
  const [currentView, setCurrentView] = useState<EditorView>()
  const [clientUuid, setClientUuid] = useState<string>(ourUuid())

  const updateView = (newView?: EditorView) => {
    currentViewRef.current = newView
    setCurrentView(newView)
  }

  const resetClientUuid = () => {
    setClientUuid(ourUuid())
  }

  return (
    <ProseContext.Provider value={{ updateView, currentViewRef, currentView, clientUuid, resetClientUuid }}>
      {children}
    </ProseContext.Provider>
  )
}

export const useProseInstance = (throwError = true) => {
  const context = useContext(ProseContext)
  if (context === undefined && throwError) {
    throw new Error('useProseInstance must be used within a Prose Instance Provider')
  }
  return context
}

export const ProseConsumer = ({ children }: { children: any }) => {
  return (
    <ProseContext.Consumer>
      {(context) => {
        if (context === undefined) {
          throw new Error('ProseConsumer must be used within a Prose Instance Provider')
        }
        return children(context)
      }}
    </ProseContext.Consumer>
  )
}

export default ProseInstanceProvider
